import { useCallback, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { PrivateRoutes } from '../../routes/navigation';
import { ImpactStyle } from '@capacitor/haptics';
import {
  createConsultationByAvaialableCouponBalance,
  createConsultationByAvailableBalance,
} from '../../api/consultation';
import { useDispatch, useSelector } from 'react-redux';
import { consultationsSelector } from '../../redux/consultations/selector';
import { subscriptionsSelector } from '../../redux/subscriptions/selector';
import { makeid } from '../../pages/PaymentPage/utils';
import { DateTime } from 'luxon';
import { getChatRoomsListBySpecialistId } from '../../api/agora/chatRooms';
import { ChatRoomsData } from '../../redux/chatRooms/types';
import { ConsultationType, SystemMessagesType, UserType } from '../../pages/types';
import { formatSystemMessage } from '../../context/AgoraContext/formatSystemMessage';
import { consultationTimeToFormatSeconds } from '../../pages/SchedulePage/utils';
import EmailSystem from '../../api/emails';
import { addNew } from '../../redux/consultations/reducer';
import { fetchSubscriptions } from '../../redux/subscriptions/thunk';
import { getUsersPsychologistsListThunk } from '../../redux/usersPsychologists/action';
import useAgoraRtm from '../../context/AgoraContext/AgoraContext';
import { useIntl } from 'react-intl';
import useLangs from '../../context/LangsContext';
import { Browser } from '@capacitor/browser';
import { ISubscription } from '../../redux/subscriptions/types';
import useToast from '../../context/ToastContext/ToastContext';
import { fetchSpecialistByID } from 'src/redux/loadedSpecialists/thunk';
import { useTranslation } from 'react-i18next';
import { useIsClientCan } from 'src/shared/hooks/useIsClientCan';
import { isomorficHapticsImpact } from 'src/shared/utils/haptics';
import { getCouponBalanceAvailability } from 'src/api/coupons';
import { updateUserPsychologist } from 'src/redux/user/reducer';
import { SUBSCRIPTION_PAUSE } from 'src/intercom-articles';

export const useAppointment = () => {
  const history = useHistory();
  const dispatch = useDispatch();
  const intl = useIntl();
  const { showToast } = useToast();

  const { sendSystemMessage } = useAgoraRtm();
  const { locale } = useLangs();
  const { t } = useTranslation('ui');

  const [isPushToAppointmentLoading, setIsPushToAppointmentLoading] = useState(false);
  const [bookingActionLoadingStatus, setBookingActionLoadingStatus] = useState<null | 'start' | 'success'>(null);
  const [paymentStatus, setPaymentStatus] = useState<null | 'paid' | 'unknown'>(null);

  const currentUser = useSelector((state: any) => state.user);
  const subscriptions = useSelector(subscriptionsSelector());
  const consultations = useSelector(consultationsSelector);
  const { isClientCan } = useIsClientCan();

  /**
   * @private
   */
  const _getConsultationsBySpecialistID = (specialistID: string) => {
    return consultations?.filter((elem) => elem.psychologist_id === specialistID) ?? [];
  };

  /**
   * @private
   */
  const _getActiveSubscription = useCallback(
    (specialistID: string) => {
      return (
        subscriptions?.data?.find((el) => {
          return el.subscription_status === 'active' && el.therapist_id === specialistID;
        }) ?? null
      );
    },
    [subscriptions?.data?.length, subscriptions?.data?.reduce((acc, el) => acc + el.used_balance, 0)]
  );

  /**
   * @private
   */
  const _getSubscriptionAvailableBalance = useCallback(
    (activeSubscription?: ISubscription | null) => {
      if (!activeSubscription) return 0;
      return activeSubscription.session_count - activeSubscription.used_balance;
    },
    [subscriptions?.data?.length, subscriptions?.data?.reduce((acc, el) => acc + el.used_balance, 0)]
  );

  const handlePostBookingAction = useCallback(
    async (userID: string, specialistID: string, consultationData: ConsultationType) => {
      try {
        const { data: roomData } = await getChatRoomsListBySpecialistId(userID, specialistID);

        await sendSystemMessage({
          roomData: roomData?.[0] as ChatRoomsData,
          specialistId: specialistID,
          user: currentUser as Omit<UserType, 'id'> & { id: string },
          message: formatSystemMessage(
            SystemMessagesType.session_booked,
            {
              display_name: currentUser?.display_name as string,
              consultation_time: consultationData.consultation_time as string,
            },
            intl
          ),
          notifyMessage: `${intl.formatMessage({
            id: 'booking.notify_message.booked',
            defaultMessage: 'Заброньовав(ла) сеанс на',
          })} ${DateTime.fromSeconds(consultationTimeToFormatSeconds(consultationData.consultation_time))
            .setZone(consultationData.psychologist_timezone)
            .setLocale(locale)
            .toFormat('dd MMMM HH:mm')}`,
          locale,
        });

        await EmailSystem.sendSessionBooked(consultationData.id);

        dispatch(addNew(consultationData));
        await dispatch(fetchSubscriptions(userID));
        await dispatch(getUsersPsychologistsListThunk());
      } catch (error) {}
    },
    []
  );

  /**
   * @private
   */
  const _createConsultationBySubscriptionBalance = useCallback(
    async (userID: string, specialistID: string, consultationTimeUTCInISO: string) => {
      try {
        setBookingActionLoadingStatus('start');

        const activeSubscription = _getActiveSubscription(specialistID);

        if (!activeSubscription?.id) throw new Error('no_active_subscription');

        const consultationData = await createConsultationByAvailableBalance({
          userTimezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
          userID: userID,
          consultationID: makeid(16),
          psychologistID: specialistID,
          consultationTime: consultationTimeUTCInISO,
          videochatRoomID: makeid(20),
          subscriptionID: activeSubscription?.id,
        });

        await handlePostBookingAction(userID, specialistID, consultationData);
        setBookingActionLoadingStatus('success');
      } catch (error: any) {
        showToast({ error });
        history.push(PrivateRoutes.CONSULTATION);
        setBookingActionLoadingStatus(null);
      }
    },
    [subscriptions?.data?.length, currentUser?.id]
  );

  const createConsultationByCouponBalance = useCallback(
    async (userID: string, specialistID: string, consultationTimeUTCInISO: string, consultationID?: string) => {
      try {
        setBookingActionLoadingStatus('start');
        const consultationData = await createConsultationByAvaialableCouponBalance({
          userTimezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
          userID: userID,
          consultationID: consultationID ?? makeid(16),
          psychologistID: specialistID,
          consultationTime: consultationTimeUTCInISO,
          videochatRoomID: makeid(20),
        });

        await handlePostBookingAction(userID, specialistID, consultationData);
        dispatch(updateUserPsychologist(specialistID));
        EmailSystem.sendSessionBooked(consultationData.id);

        setPaymentStatus('paid');
        setBookingActionLoadingStatus('success');
      } catch (error) {
        showToast({ error });
        history.push(PrivateRoutes.CONSULTATION);
        setBookingActionLoadingStatus(null);
      }
    },
    [currentUser?.id]
  );

  const checkIsSubscriptionAvailable = useCallback(
    (specialistId: string) => {
      const activeSubscription = _getActiveSubscription(specialistId);
      const subscriptionAvailableBalance = _getSubscriptionAvailableBalance(activeSubscription);
      return !!activeSubscription && subscriptionAvailableBalance > 0;
    },
    [_getActiveSubscription, _getSubscriptionAvailableBalance]
  );

  const _getRoute = useCallback(
    (specialistId: string) => {
      let route: string | undefined;
      const specialistConsultations = _getConsultationsBySpecialistID(specialistId);
      const isSubscriptionAvailable = checkIsSubscriptionAvailable(specialistId);

      if (isSubscriptionAvailable) {
        route = PrivateRoutes.SUBSCRIPTION_BOOKING;
      } else if (!!specialistConsultations?.length) {
        route = PrivateRoutes.SUBSCRIPTION;
      } else {
        route = PrivateRoutes.BOOKING_SYSTEM;
      }

      return route;
    },
    [_getConsultationsBySpecialistID, checkIsSubscriptionAvailable]
  );

  const _getReplacedRoute = useCallback(
    (route: string, specialistId: string) => {
      const activeSubscription = _getActiveSubscription(specialistId);
      const replacedRoute = route
        .replace(':id', specialistId ?? '')
        .replace(':consultationID?', '')
        .replace(':subscriptionID', activeSubscription?.id ?? '');

      return replacedRoute;
    },
    [_getActiveSubscription]
  );

  const _pushToAppointmentRoute = useCallback(
    (route: string) => {
      if (route.includes('https://'))
        Browser.open({
          url: route,
          presentationStyle: 'fullscreen',
          windowName: '_self',
        });
      else history.push(route);
    },
    [history]
  );

  const _checkIfUserCanBookSession = useCallback(
    async (specialistId: string) => {
      await dispatch(fetchSpecialistByID(specialistId)).unwrap();
      const canBookSession = isClientCan({
        action: 'book_session',
        specialistId,
      });

      if (!canBookSession) {
        showToast({
          text: t('not_active_specialist_tost.title'),
        });
        return false;
      }
      return true;
    },
    [isClientCan, dispatch, showToast, t]
  );

  const pushToAppointmentPage = useCallback(
    async (specialistId: string) => {
      isomorficHapticsImpact({ style: ImpactStyle.Light });
      const isCanBookSession = await _checkIfUserCanBookSession(specialistId);
      if (!isCanBookSession) return;

      const route = _getRoute(specialistId);
      const replacedRoute = _getReplacedRoute(route, specialistId);
      return _pushToAppointmentRoute(replacedRoute);
    },
    [_checkIfUserCanBookSession, _getRoute, _getReplacedRoute, _pushToAppointmentRoute]
  );

  const checkSubscriptionAvailable = useCallback(
    async (specialistID: string) => {
      const activeSubscription = _getActiveSubscription(specialistID);
      const localSubscriptions = await dispatch(fetchSubscriptions(currentUser?.id)).unwrap();
      const localActiveSubscription = localSubscriptions.find(
        (el) => el.subscription_status === 'active' && el.therapist_id === specialistID
      );

      if (localActiveSubscription?.pause_collection && activeSubscription) {
        showToast({
          text: intl.formatMessage({
            id: 'schedule.alert.pause',
            defaultMessage: 'Ваша підписка призупинена. Відновіть її, щоб записатися на сеанс.',
          }),
          intercomArticleID: SUBSCRIPTION_PAUSE,
        });
        return false;
      }
      return true;
    },
    [_getActiveSubscription, dispatch, currentUser?.id, showToast, intl, history]
  );

  const bookingActionHandler = useCallback(
    async (specialistID: string, bookingTimeUTCInISO: string, checkSubscription = false) => {
      isomorficHapticsImpact({ style: ImpactStyle.Light });
      // if checkSubscription is true, we need to check if the user can book a session and if they don't need buy subscription
      if (checkSubscription) {
        setBookingActionLoadingStatus('start');
        const [isCanBookSession, isSubscriptionAvailable] = await Promise.all([
          _checkIfUserCanBookSession(specialistID),
          checkSubscriptionAvailable(specialistID),
        ]);
        if (!isCanBookSession || !isSubscriptionAvailable) {
          setBookingActionLoadingStatus(null);
          return;
        }
        const possibleRedirectRoute = _getRoute(specialistID);
        // only if route is SUBSCRIPTION, we need to redirect to the subscription page, otherwise we need book a session
        if (possibleRedirectRoute === PrivateRoutes.SUBSCRIPTION) {
          setBookingActionLoadingStatus(null);
          return _pushToAppointmentRoute(_getReplacedRoute(possibleRedirectRoute, specialistID));
        }
      }

      const couponCoverageCheckResponse = await getCouponBalanceAvailability(specialistID);

      const luxonObject = DateTime.fromISO(bookingTimeUTCInISO);
      const userID = currentUser?.id;
      const isSubscriptionAvailable = checkIsSubscriptionAvailable(specialistID);

      if (isSubscriptionAvailable) {
        return _createConsultationBySubscriptionBalance(userID, specialistID, bookingTimeUTCInISO);
      }

      if (couponCoverageCheckResponse?.is_coupon_cover_session_exact) {
        return createConsultationByCouponBalance(userID, specialistID, bookingTimeUTCInISO);
      }

      if (checkSubscription) {
        setBookingActionLoadingStatus(null);
      }

      return history.push(
        PrivateRoutes.PAYMENT.replace(':id', specialistID)
          .replace(':date?', luxonObject.toISODate())
          .replace(':time?', luxonObject.toFormat('HH:mm'))
      );
    },
    [
      subscriptions?.data?.length,
      consultations?.length,
      currentUser?.id,
      _getActiveSubscription,
      _getSubscriptionAvailableBalance,
      _checkIfUserCanBookSession,
      _getRoute,
      _getReplacedRoute,
      _pushToAppointmentRoute,
      history,
      createConsultationByCouponBalance,
      checkIsSubscriptionAvailable,
      _createConsultationBySubscriptionBalance,
    ]
  );

  const clearStates = useCallback(() => {
    setBookingActionLoadingStatus(null);
    setIsPushToAppointmentLoading(false);
    setPaymentStatus(null);
  }, []);

  return Object.freeze({
    pushToAppointmentPage,
    isPushToAppointmentLoading,
    bookingActionLoadingStatus,
    setBookingActionLoadingStatus,
    bookingActionHandler,
    clearStates,
    createConsultationByCouponBalance,
    checkIsSubscriptionAvailable,
    paymentStatus,
  });
};
